import { useMemo, useState } from 'react';

import { FileStackIcon, NotebookPenIcon } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import {
  FormCheckbox,
  FormCreatableSelect,
  FormField,
  FormInput,
  FormItem,
  FormMessage,
  FormSelect,
  Label,
} from '@/_components';
import {
  addressesIncludeKey,
  CardContainer,
  FormFilesInput,
  FormLayout,
  SectionHeading,
  SelectorWithAvatarContainer,
} from '@/_containers';
import { RichText } from '@/_containers/inputs/rich-text';
import { AsyncCompanyWithStateSelector } from '@/_pages/companies/shared/components/company-async-selector-with-address';
import { AsyncContactSelector } from '@/_pages/contacts/shared';
import { allowedFileTypes, nameResolver, showFormField } from '@/_shared';

import { AddressSection } from './address-section';
import { deliveryMethodOptions } from './delivery-method';
import { DownloadPickTicketsPDF } from './download-pdf-button';
import { InventoryKitItemsSection } from './pick-ticket-items/pick-tickets-items-section';
import { PickerCombobox } from './picker-combobox';
import { removalCauseOptions } from './removal-cause';
import { statusOptions } from './status';

import type { PickTicketsFormSchema } from '../schemas';

export function FormFields({
  id,
  form,
  isEditable = true,
  isProfile = false,
  onRequestEdit = () => {},
  autoFocus = false,
}: {
  id: string;
  form: UseFormReturn<PickTicketsFormSchema>;
  isEditable?: boolean;
  isProfile?: boolean;
  onRequestEdit?: () => void;
  autoFocus?: boolean;
}) {
  const [contactRef, setContactRef] = useState<HTMLInputElement | null>(null);
  const folder_path = `pick-tickets/${id}`;
  const [notes, attached_documents, client, contacts, why_removed_comment, remove_items, pick_number] = form.watch([
    'notes',
    'attached_documents',
    'client',
    'contacts',
    'why_removed_comment',
    'remove_items',
    'pick_number',
  ]);

  const { uniqueClientContacts, contactValue } = useMemo(() => {
    const uniqueContactsMap = new Map();
    (client?.contacts ?? []).forEach((c) => {
      if (c.id) {
        uniqueContactsMap.set(c.id, c);
      }
    });

    const uniqueClientContacts = Array.from(uniqueContactsMap.values());

    // Filter contacts to only include those available in contacts
    const contactValue = client ? (uniqueClientContacts.find((c) => c.id === contacts?.id) ?? null) : contacts;

    return { uniqueClientContacts, contactValue };
  }, [client, contacts]);

  return (
    <>
      <CardContainer>
        {isProfile && (
          <section className="flex items-center justify-between">
            <div className="text-c-card-muted text-xs font-semibold">Picker #: {pick_number || '—'}</div>
            <DownloadPickTicketsPDF id={`${id}`} />
          </section>
        )}

        <FormLayout className="mt-12">
          <div className="col-span-12 sm:col-span-8 md:col-span-12 lg:col-span-6">
            <FormField
              control={form.control}
              name="picker"
              render={() => (
                <FormItem>
                  <PickerCombobox disabled={!isEditable} autoFocus={autoFocus} /> <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="status"
            render={({ field: { value, onChange } }) => (
              <FormSelect
                formItemClassName="col-span-12 sm:col-span-4 md:col-span-12 lg:col-span-6"
                disabled={!isEditable}
                value={value}
                getOptionLabel={(option) => option.name}
                onChange={onChange}
                options={statusOptions}
                label="Status"
              />
            )}
          />

          <SelectorWithAvatarContainer
            disabled={!isEditable}
            avatarClassName="h-9 w-11 rounded-md"
            src={client?.logo?.at(0)?.url || ''}
            label="Company"
            name={client?.name ?? ''}
            className="col-span-12 sm:col-span-6 lg:flex-row 2xl:col-span-6"
          >
            <FormField
              control={form.control}
              name="client"
              render={({ field }) => (
                <AsyncCompanyWithStateSelector
                  label="Company"
                  value={field.value}
                  disabled={!isEditable}
                  onValueChange={(e) => {
                    field.onChange(e);
                    // reset contacts when client changes
                    form.setValue('contacts', null, {
                      shouldDirty: true,
                    });

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore clear contact value
                    contactRef?.clearValue();
                  }}
                  apiParams={{ include: `contacts.profile_image,logo,${addressesIncludeKey()}` }}
                />
              )}
            />
          </SelectorWithAvatarContainer>

          <SelectorWithAvatarContainer
            label="Contact"
            disabled={!isEditable}
            src={contactValue?.profile_image?.at(0)?.url || ''}
            name={nameResolver(contactValue?.first_name, contactValue?.last_name)}
            className="col-span-12 sm:col-span-6 lg:flex-row 2xl:col-span-6"
          >
            <FormField
              control={form.control}
              name="contacts"
              render={({ field: { onChange, value } }) =>
                client ? (
                  <FormSelect
                    disabled={!isEditable}
                    value={contactValue}
                    getOptionLabel={(option) => nameResolver(option.first_name, option.last_name)}
                    onChange={onChange}
                    options={uniqueClientContacts}
                    label="Contact"
                  />
                ) : (
                  <AsyncContactSelector
                    label="Contact"
                    value={value}
                    disabled={!isEditable}
                    onValueChange={onChange}
                    apiParams={{ include: 'profile_image,logo' }}
                    getRef={(e) => setContactRef(e)}
                  />
                )
              }
            />
          </SelectorWithAvatarContainer>

          <FormField
            control={form.control}
            name="delivery_method"
            render={({ field: { value, onChange } }) => (
              <FormCreatableSelect
                isClearable
                value={value}
                label="Delivery Method"
                onChange={onChange}
                options={deliveryMethodOptions}
                formItemClassName="col-span-12 sm:col-span-6 2xl:col-span-6"
                getOptionLabel={(item) => item.name}
                getNewOptionData={(inputValue: string) => ({ name: inputValue, value: inputValue })}
                disabled={!isEditable}
              />
            )}
          />

          <FormField
            control={form.control}
            name="tracking_number"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 2xl:col-span-6"
                disabled={!isEditable}
                value={value}
                onChange={onChange}
                label="Tracking Number"
              />
            )}
          />

          <div className="xs:col-span-4 col-span-12 flex items-end sm:col-span-6 md:col-span-4 2xl:col-span-3">
            <FormField
              control={form.control}
              name="remove_items"
              render={({ field }) => (
                <FormCheckbox
                  label="Remove Items"
                  disabled={!isEditable}
                  checked={!!field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    if (!e.target.checked) {
                      form.setValue('why_removed', null);
                      form.setValue('why_removed_comment', '');
                    }
                  }}
                />
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="why_removed"
            render={({ field: { value, onChange } }) => (
              <FormSelect
                formItemClassName="xs:col-span-8 col-span-12 sm:col-span-6 md:col-span-4 2xl:col-span-3"
                disabled={!isEditable || !remove_items}
                value={value}
                getOptionLabel={(option) => option.name}
                onChange={onChange}
                options={removalCauseOptions}
                label="Why removed"
              />
            )}
          />

          {showFormField(isEditable, why_removed_comment.trim()) && (
            <div className="col-span-12">
              <Label className="mb-1.5 text-sm font-medium text-gray-700">Memo</Label>
              <FormField
                control={form.control}
                name="why_removed_comment"
                render={({ field }) => (
                  <RichText
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isEditable}
                    inline={!isEditable}
                    mediaFolderPath={folder_path}
                  />
                )}
              />
            </div>
          )}
        </FormLayout>
      </CardContainer>

      <InventoryKitItemsSection isEditable={isEditable} />

      <AddressSection isEdit={isEditable} onRequestEdit={onRequestEdit} />

      {showFormField(isEditable, (attached_documents?.length ?? []) > 0) && (
        <CardContainer>
          <SectionHeading text="Attached Documents" icon={FileStackIcon} />

          <FormField
            control={form.control}
            name="attached_documents"
            render={({ field: { value, onChange } }) => (
              <FormFilesInput
                folder_path={folder_path}
                value={value}
                disabled={!isEditable}
                onChange={onChange}
                formItemClassName="mt-6"
                allowedTypes={allowedFileTypes.all}
              />
            )}
          />
        </CardContainer>
      )}

      {showFormField(isEditable, notes.trim()) && (
        <CardContainer>
          <SectionHeading text="Notes" icon={NotebookPenIcon} />
          <FormLayout className="mt-8">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <RichText
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!isEditable}
                  inline={!isEditable}
                  mediaFolderPath={folder_path}
                  className="col-span-12"
                />
              )}
            />
          </FormLayout>
        </CardContainer>
      )}
    </>
  );
}
